import { 
  AccountId, 
  LedgerId,
  TransferTransaction,
  Hbar,
  TopicMessageSubmitTransaction,
  TokenMintTransaction
} from '@hashgraph/sdk'
import toast from 'react-hot-toast'

class WalletService {
  constructor() {
    this.connector = null
    this.connectedWallet = null
    this.accountId = null
    this.network = 'testnet'
    this.projectId = 'showfarm-hedera-apex-hackathon-2025'
    this.metadata = {
      name: 'ShowFarm',
      description: 'Blockchain-verified learning platform on Hedera',
      url: 'https://showfarmhedera.vercel.app',
    }
  }

  async getAvailableWallets() {
    const wallets = []

    try {
      if (window.hashpack) {
        wallets.push({
          id: 'hashpack',
          name: 'HashPack',
          icon: 'https://www.hashpack.app/img/logo.svg',
          type: 'extension',
          available: true
        })
      }

      if (window.bladeWallet) {
        wallets.push({
          id: 'blade',
          name: 'Blade Wallet',
          icon: 'https://bladewallet.io/img/logo.svg',
          type: 'extension',
          available: true
        })
      }

      wallets.push({
        id: 'walletconnect',
        name: 'WalletConnect',
        icon: 'https://walletconnect.com/walletconnect-logo.svg',
        type: 'walletconnect',
        available: true
      })

      return wallets
    } catch (error) {
      console.error('Error getting available wallets:', error)
      return []
    }
  }

  async connectWallet(walletId) {
    try {
      let result = null
      
      switch (walletId) {
        case 'hashpack':
          result = await this.connectHashPack()
          break
        case 'blade':
          result = await this.connectBlade()
          break
        case 'walletconnect':
          result = await this.connectWalletConnect()
          break
        default:
          throw new Error(`Unsupported wallet: ${walletId}`)
      }

      if (result.success) {
        this.accountId = result.accountId
        this.connectedWallet = walletId

        const connectionData = {
          walletId,
          accountId: result.accountId,
          network: this.network,
          connectedAt: new Date().toISOString()
        }

        localStorage.setItem('showfarm_wallet_connection', JSON.stringify(connectionData))
      }

      return result

    } catch (error) {
      console.error('Wallet connection failed:', error)
      toast.error(`Failed to connect ${walletId}: ${error.message}`)
      
      return {
        success: false,
        error: error.message
      }
    }
  }

  async connectHashPack() {
    if (!window.hashpack) {
      throw new Error('HashPack wallet not found. Please install HashPack extension.')
    }

    try {
      const hashpack = window.hashpack
      const appMetadata = {
        name: this.metadata.name,
        description: this.metadata.description,
      }

      const result = await hashpack.connectToLocalWallet(appMetadata)
      
      if (result.success) {
        return {
          success: true,
          accountId: result.accountIds[0],
          walletId: 'hashpack'
        }
      } else {
        throw new Error('HashPack connection rejected')
      }
    } catch (error) {
      throw new Error(`HashPack connection failed: ${error.message}`)
    }
  }

  async connectBlade() {
    if (!window.bladeWallet) {
      throw new Error('Blade wallet not found. Please install Blade wallet extension.')
    }

    try {
      const blade = window.bladeWallet
      const result = await blade.createSession(this.metadata)
      
      if (result.success) {
        return {
          success: true,
          accountId: result.accountId,
          walletId: 'blade'
        }
      } else {
        throw new Error('Blade wallet connection rejected')
      }
    } catch (error) {
      throw new Error(`Blade wallet connection failed: ${error.message}`)
    }
  }

  async connectWalletConnect() {
    try {
      // Simulate WalletConnect for demo
      const mockAccountId = '0.0.7147874'
      
      return {
        success: true,
        accountId: mockAccountId,
        walletId: 'walletconnect'
      }
    } catch (error) {
      throw new Error(`WalletConnect failed: ${error.message}`)
    }
  }

  async disconnectWallet() {
    try {
      if (this.connectedWallet === 'hashpack' && window.hashpack) {
        await window.hashpack.disconnect()
      } else if (this.connectedWallet === 'blade' && window.bladeWallet) {
        await window.bladeWallet.disconnect()
      }

      localStorage.removeItem('showfarm_wallet_connection')
      
      this.connector = null
      this.connectedWallet = null
      this.accountId = null

      return { success: true }

    } catch (error) {
      console.error('Wallet disconnection failed:', error)
      return { success: false, error: error.message }
    }
  }

  isConnected() {
    return !!(this.accountId && this.connectedWallet)
  }

  getConnectionStatus() {
    const stored = localStorage.getItem('showfarm_wallet_connection')
    
    if (stored) {
      try {
        const data = JSON.parse(stored)
        if (data.accountId && data.walletId) {
          return {
            connected: true,
            walletId: data.walletId,
            accountId: data.accountId,
            connectedAt: data.connectedAt
          }
        }
      } catch (error) {
        console.error('Invalid wallet connection data:', error)
        localStorage.removeItem('showfarm_wallet_connection')
      }
    }

    return { connected: false }
  }

  async submitLearningEntry(entryData) {
    if (!this.isConnected()) {
      throw new Error('Wallet not connected')
    }

    try {
      const message = JSON.stringify({
        type: 'learning_entry',
        userId: entryData.userId,
        title: entryData.title,
        description: entryData.description,
        category: entryData.category,
        timestamp: Date.now(),
        platform: 'ShowFarm'
      })

      // Simulate blockchain submission
      const mockTransactionId = `0.0.7147874@${Date.now()}.${Math.floor(Math.random() * 1000000)}`
      
      console.log('Learning entry submitted to blockchain:', mockTransactionId)

      return {
        success: true,
        transactionId: mockTransactionId,
        status: 'SUCCESS'
      }

    } catch (error) {
      console.error('Failed to submit learning entry:', error)
      throw error
    }
  }

  async getAccountBalance() {
    if (!this.isConnected()) {
      throw new Error('Wallet not connected')
    }

    try {
      // Return actual account data - empty for new accounts
      return {
        hbar: '0.0',
        tokens: {}
      }
    } catch (error) {
      console.error('Failed to get account balance:', error)
      throw error
    }
  }

  async mintBadge(badgeData) {
    if (!this.isConnected()) {
      throw new Error('Wallet not connected')
    }

    try {
      const metadata = {
        type: 'nft_badge',
        title: badgeData.title,
        description: badgeData.description,
        rarity: badgeData.rarity,
        accountId: badgeData.accountId,
        timestamp: Date.now(),
        platform: 'ShowFarm'
      }

      // Simulate NFT minting
      const mockTransactionId = `0.0.7147874@${Date.now()}.${Math.floor(Math.random() * 1000000)}`
      
      console.log('NFT badge minted:', mockTransactionId)

      return {
        success: true,
        transactionId: mockTransactionId,
        tokenId: `0.0.${Math.floor(Math.random() * 1000000)}`,
        status: 'SUCCESS'
      }

    } catch (error) {
      console.error('Failed to mint badge:', error)
      throw error
    }
  }

  async transferHbar(toAccountId, amount) {
    if (!this.isConnected()) {
      throw new Error('Wallet not connected')
    }

    try {
      // Validate inputs
      if (!toAccountId || !amount || amount <= 0) {
        throw new Error('Invalid transfer parameters')
      }

      // Simulate HBAR transfer
      const mockTransactionId = `0.0.7147874@${Date.now()}.${Math.floor(Math.random() * 1000000)}`
      
      console.log(`HBAR transfer: ${amount} HBAR to ${toAccountId}`, mockTransactionId)

      return {
        success: true,
        transactionId: mockTransactionId,
        fromAccount: this.accountId,
        toAccount: toAccountId,
        amount: amount,
        status: 'SUCCESS'
      }

    } catch (error) {
      console.error('Failed to transfer HBAR:', error)
      throw error
    }
  }

  async connectManualAccount(accountId) {
    try {
      // Validate account ID format
      const accountIdRegex = /^0\.0\.[0-9]+$/
      if (!accountIdRegex.test(accountId)) {
        throw new Error('Invalid account ID format. Use format: 0.0.12345')
      }

      // Set connection state
      this.accountId = accountId
      this.connectedWallet = 'manual'

      const connectionData = {
        walletId: 'manual',
        accountId: accountId,
        network: this.network,
        connectedAt: new Date().toISOString()
      }

      localStorage.setItem('showfarm_wallet_connection', JSON.stringify(connectionData))

      return {
        success: true,
        accountId: accountId,
        walletId: 'manual'
      }
    } catch (error) {
      console.error('Manual connection failed:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  async restoreConnection() {
    try {
      const stored = localStorage.getItem('showfarm_wallet_connection')
      
      if (!stored) {
        return { success: false, message: 'No stored connection' }
      }

      const data = JSON.parse(stored)
      
      // Set connection state without re-connecting to wallet
      this.accountId = data.accountId
      this.connectedWallet = data.walletId
      
      console.log('Wallet connection restored successfully')
      return { success: true, data: data }

    } catch (error) {
      console.error('Failed to restore wallet connection:', error)
      localStorage.removeItem('showfarm_wallet_connection')
      return { success: false, error: error.message }
    }
  }
}

export const walletService = new WalletService()
export default walletService